import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';

import ProjectVitalFinanceMovementDialog from './ProjectVitalFinanceMovementDialog';
import ProjectVitalIssueDialog from './ProjectVitalIssueDialog';
import ProjectVitalTaskDialog from './ProjectVitalTaskDialog';

type Props = {
  open: boolean;
  onClose: () => void;
};

const options = [
  {
    key: 'task',
    label: 'Tarea',
    description: 'Algo que necesitas hacer.',
    icon: <TaskAltRoundedIcon />,
    enabled: true,
  },
  {
    key: 'movement',
    label: 'Gasto / ingreso',
    description: 'Registrar gasto, ingreso o transferencia.',
    icon: <PaymentsRoundedIcon />,
    enabled: true,
  },
  {
    key: 'issue',
    label: 'Asunto',
    description: 'Una situación que necesita seguimiento.',
    icon: <FolderOpenRoundedIcon />,
    enabled: true,
  },
  {
    key: 'decision',
    label: 'Decisión',
    description: 'Guardar una decisión y su motivo.',
    icon: <GavelRoundedIcon />,
    enabled: false,
  },
  {
    key: 'note',
    label: 'Nota',
    description: 'Conservar información de forma rápida.',
    icon: <NotesRoundedIcon />,
    enabled: false,
  },
];

export default function ProjectVitalAddDialog({
  open,
  onClose,
}: Props) {
  const [taskOpen, setTaskOpen] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  const handleOptionClick = (key: string) => {
    if (key === 'task') {
      onClose();
      setTaskOpen(true);
      return;
    }

    if (key === 'movement') {
      onClose();
      setMovementOpen(true);
      return;
    }

    if (key === 'issue') {
      onClose();
      setIssueOpen(true);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: '#111c2f',
            border: '1px solid rgba(148,163,184,0.16)',
            borderRadius: 3.5,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              flexGrow: 1,
              fontSize: '1.1rem',
              fontWeight: 850,
            }}
          >
            Añadir
          </Typography>

          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            size="small"
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 1.5, pb: 1.5 }}>
          <List disablePadding>
            {options.map((option) => (
              <ListItemButton
                key={option.key}
                disabled={!option.enabled}
                onClick={() => handleOptionClick(option.key)}
                sx={{
                  mb: 0.65,
                  borderRadius: 2.5,
                  border: '1px solid rgba(148,163,184,0.10)',
                  bgcolor: option.enabled
                    ? 'rgba(255,255,255,0.018)'
                    : 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(77,208,225,0.06)',
                  },
                  '&.Mui-disabled': {
                    opacity: 0.42,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 42,
                    color: option.enabled ? '#4DD0E1' : '#64748b',
                  }}
                >
                  {option.icon}
                </ListItemIcon>

                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  primaryTypographyProps={{
                    fontWeight: 750,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.8rem',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>

      <ProjectVitalTaskDialog
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
      />

      <ProjectVitalFinanceMovementDialog
        open={movementOpen}
        onClose={() => setMovementOpen(false)}
      />

      <ProjectVitalIssueDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
      />
    </>
  );
}
